// /src/components/tool/ColorPicker.jsx
// Ceci est la version avec les gradients.

import React, { useState, useEffect, useRef, useCallback } from 'react';
//  utility to inject CSS dynamically
import { createDynamicCSS } from '../../../utils/CreateDynamicCSS';

//  base palette for quick selection
const COLORS = [
  "#4b0505", "#850808", "#D10505", "#F5A623", "#F8E71C",
  "#8B572A", "#7ED321", "#417505", "#BD10E0", "#9013FE",
  "#4A90E2", "#50E3C2", "#B8E986", "#000000", "#4A4A4A",
  "#DAB8DF", "#FFFFFF"
];


const getColorClass = (hex) => {
  if (!hex) return '';
  const safe = hex.replace('#','').replace(/[^0-9a-zA-Z]/g,'');
  const className = `color-${safe}`;
  //console.log('[ColorPicker] Generating color class for', hex, '->', className);
  createDynamicCSS(className, { backgroundColor: hex });
  return className;
};

const getPresetClass = (hex) => {
  const safe = hex.replace('#','').replace(/[^0-9a-zA-Z]/g,'');
  const className = `preset-${safe}`;
  //console.log('[ColorPicker] Generating preset class for', hex, '->', className);
  createDynamicCSS(
    className,
    { backgroundColor: hex }
  );
  return className;
};

//  convert HSB to hex string
const hsbToHex = (h, s, b) => {
  const k = n => (n + h/60) % 6;
  const f = n => b * (1 - s * Math.max(0, Math.min(k(n), 4 - k(n), 1)));
  const rgb = [f(5),f(3),f(1)].map(x => Math.round(x*255));
  return `#${rgb.map(v=>v.toString(16).padStart(2,'0')).join('')}`;
};

//  convert hex string to HSL for initial positioning
const hexToHsl = (H) => {
  let r = parseInt(H.slice(1,3),16)/255,
      g = parseInt(H.slice(3,5),16)/255,
      b = parseInt(H.slice(5,7),16)/255;
  const max = Math.max(r,g,b), min = Math.min(r,g,b);
  let h = 0, s = 0, l = (max+min)/2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5
      ? d/(2 - max - min)
      : d/(max + min);
    switch(max) {
      case r: h = ((g - b)/d + (g < b ? 6 : 0)); break;
      case g: h = ((b - r)/d + 2); break;
      case b: h = ((r - g)/d + 4); break;
    }
    h *= 60;
  }
  return { h, s, l };
};

const getHueCursorClass = (hue) => {
  const percent = Math.round((hue / 360) * 100);
  createDynamicCSS(`hue-cursor-${percent}`, {
    left: `${percent}%`,
    backgroundColor: `hsl(${hue}, 100%, 50%)`
  });
  return `hue-cursor-${percent}`;
};

const ColorPicker = React.forwardRef(({
  label,
  color,
  setColor,
  gradientColors,
  setGradientColors,
  activeColorPicker,
  setActiveColorPicker,
  onToggle,
  colorPickerId,
  disabled
}, ref) => {
  const [showPicker, setShowPicker] = useState({ show: false, colorKey: null });
  const pickerRef = useRef(null);
  const [editingKey, setEditingKey] = useState(null);
  
  // close picker on outside click
  const handleClickOutside = useCallback(e => {
    if (pickerRef.current && !pickerRef.current.contains(e.target) && activeColorPicker) {
      setActiveColorPicker(null);
    }
  }, [activeColorPicker, setActiveColorPicker]);
  
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);
  
  // sync local showPicker.show with parent activeColorPicker
  useEffect(() => {
    setShowPicker(prev => ({
      show: activeColorPicker === null
        ? false
        : activeColorPicker === prev.colorKey,
      colorKey: prev.colorKey
    }));
  }, [activeColorPicker]);
  
  //  convert hex string to HSB
const hexToHsb = (hex) => {
  const r = parseInt(hex.slice(1,3),16) / 255;
  const g = parseInt(hex.slice(3,5),16) / 255;
  const b = parseInt(hex.slice(5,7),16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  //  hue calculation (same as hexToHsl)
  let h = 0;
  if (max !== min) {
    const d = max - min;
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)); break;
      case g: h = ((b - r) / d + 2); break;
      case b: h = ((r - g) / d + 4); break;
    }
    h *= 60;
  }
  //  saturation for HSV
  const s = max === 0 ? 0 : (max - min) / max;
  const v = max;
  return { h, s, v };
};


  // when click on a swatch
  const loadColor = (hex) => {
    if (!hex || hex === 'transparent') return;
    // met à jour l’input hex
    setHexInput(hex);
    // convertit en HSL
    const { h, s, v } = hexToHsb(hex);
    setHue(Math.round(h));
    setSat(Math.round(s * 100));
    setBri(Math.round(v * 100));
    // génère le curseur CSS au bon emplacement
    const satPct = Math.round(s * 100);
    const briPct = Math.round((1 - v) * 100);
    const cursorName = `cursor-${satPct}-${briPct}-${hex.replace('#','')}`;
    createDynamicCSS(cursorName, {
      left:          `${satPct}%`,
      top:           `${briPct}%`,
      backgroundColor: hex
    });
    setCursorClass(cursorName);
  };
  
  // 2) Au clic sur un swatch (qu’il soit « primary » ou un gradient)  
  const toggle = (key) => {
    const hex = key === 'primary'
      ? color
      : (gradientColors?.[key] || '');

    loadColor(hex);
    setEditingKey(key);
    onToggle(colorPickerId);
  };;
  
  // handler shared single or gradient
  const handleComplete = (newHex) => {
    //  update hex input value
    setHexInput(newHex);

    //  convert hex to HSB
    const { h, s, v } = hexToHsb(newHex);
    const satPct = Math.round(s * 100);
    const briPct = Math.round((1 - v) * 100);

    //  update hue, saturation, brightness state
    setHue(Math.round(h));
    setSat(satPct);
    setBri(Math.round(v * 100));

    //  update saturation gradient for current hue
    const satClass = `saturation-gradient-${Math.round(h)}`;
    createDynamicCSS(satClass, {
      background: `linear-gradient(to right, #fff, hsl(${Math.round(h)}, 100%, 50%))`
    });

    //  generate cursor CSS class at correct position
    const cursorName = `cursor-${satPct}-${briPct}-${newHex.replace('#','')}`;
    createDynamicCSS(cursorName, {
      left:            `${satPct}%`,
      top:             `${briPct}%`,
      backgroundColor: newHex
    });
    setCursorClass(cursorName);

    //  propagate the new color upstream
    if (gradientColors && typeof setGradientColors === "function") {
      setGradientColors({
        ...gradientColors,
        [editingKey]: newHex
      });
    } else {
      setColor(newHex);
    }
  };

  
  // update HSB selection

  const [hexInput, setHexInput] = useState(
    gradientColors
      ? gradientColors[showPicker.colorKey] || ''
      : color || ''
  );

  const [hue, setHue] = useState(0);
  const [sat, setSat] = useState(50);
  const [bri, setBri] = useState(50);
  const [cursorClass, setCursorClass] = useState('');
  
  const updateColor = (x, y) => {
    const rect = pickerRef.current
      .querySelector('.color-picker-saturation')
      .getBoundingClientRect();
  
    const satPercent = Math.round((x / rect.width) * 100);
    const briPercent = Math.round((y / rect.height) * 100);
  
    // Calcul de la couleur
    const briVal = 1 - briPercent / 100;             // inversion
    const newHex = hsbToHex(hue, satPercent/100, briVal);
    handleComplete(newHex);
  
    setSat(satPercent);
    setBri(briPercent);
  
    // Génère la classe dynamique
    const cursorName = `cursor-${satPercent}-${briPercent}-${newHex.replace('#','')}`;
    //console.log('[ColorPicker] Generating cursor class:', cursorName, { left: `${satPercent}%`, top: `${briPercent}%`, backgroundColor: newHex });
    createDynamicCSS(cursorName, {
      left:          `${satPercent}%`,
      top:           `${briPercent}%`, 
      backgroundColor: newHex
    });
    setCursorClass(cursorName);
  };
  
  
  const handleHueChange = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const newHue = Math.round((x / rect.width) * 360);
    setHue(newHue);
  
    // compute new color from full saturation/brightness
    const newHex = hsbToHex(newHue, 1, 1);
    handleComplete(newHex); // remonte la couleur via setColor ou setGradientColors
  
    // réinjecte la seule gradient de saturation (pas le curseur hue)
    const satClass = `saturation-gradient-${newHue}`;
    createDynamicCSS(satClass, {
      background: `linear-gradient(to right, #fff, hsl(${newHue},100%,50%))`
    });
  };


  //  inject & return CSS class for saturation gradient
  const getSaturationGradientClass = (hue) => {
    const className = `saturation-gradient-${hue}`;
    createDynamicCSS(className, {
      background: `linear-gradient(to right, #fff, hsl(${hue}, 100%, 50%))`
    });
    return className;
  };

  //  inject & return CSS class for brightness gradient
  const getBrightnessGradientClass = (hue) => {
    const className = `brightness-gradient-${hue}`;
    createDynamicCSS(className, {
      background: `linear-gradient(to top, #000, rgba(0,0,0,0))`
    });
    return className;
  };

  return (
    <div className="color-picker-container" ref={ref}>
      {label && <label>{label}</label>}

      <div className="color-picker-flex">
        {!gradientColors ? (
          <button
            className={`colorPicker ${getColorClass(color)}`}
            onClick={() => toggle('primary')}
            disabled={disabled}
          />
        ) : (
          Object.keys(gradientColors).map((key, i, arr) => {
            const isFirst = i === 0;
            const isLast  = i === arr.length - 1;
            return (
              <button
                key={key}
                className={
                  `colorPicker ${getColorClass(gradientColors[key])}` +
                  (isFirst ? ' border-left' : '') +
                  (isLast  ? ' border-right' : '') +
                  (!isFirst && !isLast ? ' middle' : '')
                }
                onClick={() => toggle(key)}
                disabled={disabled}
              />
            );
          })
        )}
      </div>

      {activeColorPicker === colorPickerId && (
        <div className="color-picker-tooltip" data-picker-id={colorPickerId} ref={pickerRef}>
          <div className="color-picker-wrapper">
            <div className="color-picker-menu">

              {/* Hex input */}
              <div className="color-picker-hex">
                <label>Hex</label>
                <input
                  type="text"
                  value={
                    !gradientColors
                      ? color
                      : (
                          editingKey === 'primary'
                            ? color
                            : gradientColors[editingKey] || ''
                        )
                  }
                  onChange={e => handleComplete(e.target.value)}
                />
              </div>

              {/* Quick palette */}
              <div className="color-picker-grid">
                {COLORS.map(c => (
                  <button
                    key={c}
                    className={getPresetClass(c)}
                    onClick={() => {
                      loadColor(c);
                      handleComplete(c);
                    }}
                    disabled={disabled}
                  />
                ))}
              </div>

              {/* Sat/Bri panel */}
              <div
                className={`color-picker-saturation hue-${hue}`}
                onClick={e => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  updateColor(e.clientX - rect.left, e.clientY - rect.top);
                }}
              >
                <div className={`color-picker-saturation-hue ${getSaturationGradientClass(hue)}`} />
                <div className={`color-picker-saturation-brightness ${getBrightnessGradientClass(hue)}`} />
                <div className={`color-picker-saturation_cursor ${cursorClass}`} />
              </div>

              {/* Hue slider */}
              <div className="color-picker-hue" onClick={handleHueChange}>
                <div className={`color-picker-hue_cursor ${getHueCursorClass(hue)}`} />
              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default ColorPicker;
